import React, { useState, useEffect, useCallback } from "react";
import jsPDF from "jspdf";
import {
  Search,
  Eye,
  Check,
  X,
  Gavel,
  Calendar,
  Clock,
  Users,
  Building,
  MapPin,
  FileText,
  Download,
  AlertTriangle,
  Trash2,
  RefreshCcw,
  ExternalLink,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Radio,
} from "lucide-react";
import adminAuctionService, {
  NormalizedAuctionRecord,
  NormalizedAuctionParticipant,
  AuctionDocument,
} from "../../../services/adminAuctionService";

import { API_BASE_URL } from "../../../services/apiConfig";
import toast from "react-hot-toast";

import "./ManageAuction.css";
import "./ManageAuctionsPro.css";

type Auction = NormalizedAuctionRecord;
const BACKEND_URL = API_BASE_URL.replace("/api", "");

const ManageAuctions: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<
    | "all"
    | "draft"
    | "upcoming"
    | "active"
    | "live"
    | "completed"
    | "cancelled"
    | "pending"
    | "approved"
    | "rejected"
  >("all");
  const [filterCategory, setFilterCategory] = useState<
    "all" | "electronics" | "machinery" | "vehicles" | "furniture" | "other"
  >("all");
  const [selectedAuction, setSelectedAuction] = useState<Auction | null>(null);
  const [endTime, setEndTime] = useState<string | null>(null);
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // const [auction, setAuction] = useState<BaseAuction | null>(null);
  const [refreshIndex, setRefreshIndex] = useState(0);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [recentlyRejected, setRecentlyRejected] = useState<Auction | null>(
    null
  );
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  // the search box is debounced so we don't query on every keystroke
  const [searchInput, setSearchInput] = useState("");

  const statusMapForFilter = (status: string) => {
    const map: Record<string, string> = {
      active: "live",
      approved: "live",
      pending: "pending",
    };
    return map[status] || status;
  };

  const fetchAuctions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await adminAuctionService.listAuctions({
        page,
        limit,
        status: filterStatus === "all" ? "" : statusMapForFilter(filterStatus),
        search: searchTerm.trim(),
      });
      setAuctions(list.auctions);
      setTotal(list.total);
      setStatusCounts(list.statusCounts || {});

      if (          
        filterStatus === "rejected" &&
        list.auctions.length === 0 &&
        recentlyRejected
      ) {
        setAuctions([recentlyRejected]);
        setTotal(1);
      }
    } catch (e: any) {
      setError(e.message || "Failed to load auctions");
    } finally {
      setLoading(false);
    }
  }, [page, limit, filterStatus, searchTerm, recentlyRejected]);

  useEffect(() => {
    fetchAuctions();
  }, [fetchAuctions, refreshIndex]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // auto-dismiss the action banner
  useEffect(() => {
    if (!actionMessage) return;
    const t = setTimeout(() => setActionMessage(null), 4000);
    return () => clearTimeout(t);
  }, [actionMessage]);

  // Helper functions
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Document download helper - Enhanced for proper binary handling
  const handleDocumentDownload = async (doc: AuctionDocument) => {
    
    const documentUrl = doc.file_path;
    const filename = doc.file_name || "document";

    // Validate document URL
    if (!documentUrl) {
      toast.error("Document URL not available. Please check the document.");
      console.error("❌ Document URL is missing:", doc);
      return;
    }

    try {
      // Show loading toast
      toast.loading(`Downloading ${filename}...`, { id: 'download-toast' });

      // Construct full URL if it's a relative path
      let fullUrl = documentUrl.trim();
      if (!fullUrl.startsWith("http")) {
        // Use BACKEND_URL (without /api) for file uploads
        fullUrl = fullUrl.startsWith("/")
          ? `${BACKEND_URL}${fullUrl}`
          : `${BACKEND_URL}/${fullUrl}`;
      }


      // Enhanced token retrieval with admin token priority
      const getAuthToken = () => {
        // Try admin tokens first for admin context
        const adminToken = sessionStorage.getItem('adminToken') || 
                          sessionStorage.getItem('admin_token') ||
                          localStorage.getItem('adminToken') ||
                          localStorage.getItem('admin_token');
        
        if (adminToken) {
          return adminToken;
        }

        // Fallback to regular user tokens
        const userToken = localStorage.getItem("authToken") || 
                         localStorage.getItem("token") || 
                         localStorage.getItem("accessToken") ||
                         sessionStorage.getItem("authToken") ||
                         sessionStorage.getItem("token");
        
        if (userToken) {
          return userToken;
        }

        console.warn("⚠️ No authentication token found");
        return null;
      };

      // Try direct download first (without authentication) for public files
      let response: Response;
      let needsAuth = false;

      try {
        response = await fetch(fullUrl, {
          method: "GET",
          mode: 'cors',
          cache: 'no-cache',
        });

        if (response.status === 401 || response.status === 403) {
          needsAuth = true;
        }
      } catch (error) {
        needsAuth = true;
      }

      // If direct download failed, try with authentication
      if (needsAuth || !response!.ok) {
        const token = getAuthToken();
        if (!token) {
          throw new Error("Authentication required but no token available");
        }

        response = await fetch(fullUrl, {
          method: "GET",
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          mode: 'cors',
          credentials: 'include',
          cache: 'no-cache'
        });
      }

      if (!response!.ok) {
        let errorMessage = "Download failed";
        if (response!.status === 404) {
          errorMessage = "File not found on server";
        } else if (response!.status === 403) {
          errorMessage = "Access denied - insufficient permissions";
        } else if (response!.status === 401) {
          errorMessage = "Authentication required - please login again";
        } else {
          errorMessage = `Server error: ${response!.status} ${response!.statusText}`;
        }
        throw new Error(errorMessage);
      }

      // Log response details for debugging
      const contentType = response!.headers.get("content-type") || "application/octet-stream";
      const contentLength = response!.headers.get("content-length");
      

      // CRITICAL: Use arrayBuffer() instead of blob() for binary files to avoid corruption
      const arrayBuffer = await response!.arrayBuffer();

      // Check if we got actual file content
      if (arrayBuffer.byteLength === 0) {
        throw new Error("Empty file received from server");
      }


      // Enhanced file extension detection
      let finalFilename = filename;
      if (!finalFilename.includes(".")) {
        if (contentType?.includes("pdf") || contentType?.includes("application/pdf")) {
          finalFilename += ".pdf";
        } else if (contentType?.includes("image/jpeg") || contentType?.includes("image/jpg")) {
          finalFilename += ".jpg";
        } else if (contentType?.includes("image/png")) {
          finalFilename += ".png";
        } else if (contentType?.includes("image")) {
          finalFilename += ".jpg";
        } else if (
          contentType?.includes("excel") ||
          contentType?.includes("spreadsheet") ||
          contentType?.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
        ) {
          finalFilename += ".xlsx";
        } else if (
          contentType?.includes("word") ||
          contentType?.includes("application/vnd.openxmlformats-officedocument.wordprocessingml.document")
        ) {
          finalFilename += ".docx";
        } else if (contentType?.includes("text/plain")) {
          finalFilename += ".txt";
        } else {
          // Extract extension from URL as fallback
          const urlParts = fullUrl.split('.');
          const urlExtension = urlParts.length > 1 ? urlParts[urlParts.length - 1].split('?')[0] : 'pdf';
          finalFilename += `.${urlExtension}`;
        }
      }


      // Determine proper content type for blob
      const finalContentType = contentType === "application/octet-stream" 
        ? (() => {
            const ext = finalFilename.toLowerCase().split('.').pop();
            const mimeTypes: Record<string, string> = {
              'pdf': 'application/pdf',
              'jpg': 'image/jpeg',
              'jpeg': 'image/jpeg',
              'png': 'image/png',
              'gif': 'image/gif',
              'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              'txt': 'text/plain',
            };
            return mimeTypes[ext || ''] || 'application/octet-stream';
          })()
        : contentType;

      // Create blob from arrayBuffer (preserves binary data integrity)
      const downloadBlob = new Blob([arrayBuffer], { 
        type: finalContentType
      });


      // Create blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(downloadBlob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = finalFilename;
      link.style.display = "none";
      
      // Force download attribute
      link.setAttribute('download', finalFilename);

      // Add to DOM and trigger download
      document.body.appendChild(link);
      
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        link.click();
        
        // Clean up after download
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
          toast.success(`✅ Downloaded ${finalFilename} successfully!`, { id: 'download-toast' });
        }, 500);
      }, 100);

    } catch (error: any) {
      console.error("❌ Download error:", error);
      toast.error(error.message || `Failed to download ${filename}. Please try again.`, { id: 'download-toast' });
      
      // Additional error logging for debugging
      if (error.name === 'NetworkError') {
        console.error("❌ Network error - check CORS configuration");
      } else if (error.name === 'TypeError') {
        console.error("❌ Type error - possible authentication issue");
      } else if (error.message?.includes('arrayBuffer')) {
        console.error("❌ Binary data corruption - server may be returning invalid response");
      }
    }
  };

  // Export functionality
  const handleExportAuctions = () => {
    if (!auctions.length) {
      alert("No auctions to export");
      return;
    }

    const headers = [
      "Title",
      "Description",
      "Auctioneer Name",
      "Auctioneer Phone",
      "Company Name",
      "Base Price (INR)",
      "Current Bid (INR)",
      "Decremental Value (INR)",
      "Status",
      "Category",
      "Start Date",
      "Start Time",
      "End Date",
      "End Time",
      "Location",
      "Auto Extension",
      "Extension Time (min)",
      "Total Participants",
      "Approved Participants",
      "Pending Participants",
      "Rejected Participants",
      "Created At",
    ];

    const rows = auctions.map((auction) => [
      auction.title,
      auction.description.replace(/,/g, ";"),
      auction.auctioneerName,
      auction.auctioneer_phone,
      auction.companyName,
      auction.basePrice,
      auction.currentBid > 0 ? auction.currentBid : 0,
      auction.decrementalValue,
      auction.status,
      auction.category,
      auction.startDate,
      auction.startTime,
      auction.endDate,
      auction.endTime,
      auction.location ? auction.location.replace(/,/g, ";") : "",
      auction.autoExtension ? "Yes" : "No",
      auction.extensionTime || "",
      auction.participants.length,
      auction.participants.filter((p) => p.status === "approved").length,
      auction.participants.filter((p) => p.status === "pending").length,
      auction.participants.filter((p) => p.status === "rejected").length,
      new Date(auction.createdAt).toLocaleString(),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((r) => r.join(",")),
    ].join("\n");
    const dataUrl =
      "data:text/csv;charset=utf-8," + encodeURIComponent(csvContent);

    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `auctions_export_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewDetails = async (auction: Auction) => {
    setDetailLoading(true);
    setSelectedAuction(auction); // Set immediately to show modal
    try {
      const full = await adminAuctionService.getAuctionById(auction.id);
      setSelectedAuction(full); // Update with full details
    } catch (e) {
      console.error('Error fetching auction details:', e);
      // Keep the basic auction data if fetch fails
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApproveAuction = async (auctionId: string) => {
    try {
      setActionMessage(null);
      await adminAuctionService.updateAuctionStatus(auctionId, "live");
      setActionMessage("Auction approved");
      setRefreshIndex((i) => i + 1);
    } catch (e: any) {
      setActionMessage(e.message || "Failed to approve");
    }
  };

  const handleRejectAuction = async (auctionId: string, reason: string) => {
    try {
      setActionMessage(null);
      await adminAuctionService.updateAuctionStatus(
        auctionId,
        "rejected",
        reason
      );
      setActionMessage("Auction rejected");
      setRefreshIndex((i) => i + 1);
    } catch (e: any) {
      setActionMessage(e.message || "Failed to reject");
    }
  };

  const handleCloseAuction = async (auctionId: string) => {
    if (
      !window.confirm(
        "Reject this auction? Users will see it was rejected by Admin."
      )
      
        
    )
      return;
    try {
      setActionMessage(null);
      const updated = await adminAuctionService.updateAuctionStatus(
        auctionId,
        "rejected",
        "Admin rejected"
      );
      setActionMessage("Auction rejected by Admin");

      setAuctions((prev) => {
        const exists = prev.some((a) => a.id === updated.id);
        const next = exists
          ? prev.map((a) => (a.id === updated.id ? updated : a))
          : [...prev, updated];
        return next;
      });
      setRecentlyRejected(updated);

      if (filterStatus !== "rejected") {
        setRefreshIndex((i) => i + 1);
      } else {
        setTotal((t) => (auctions.length ? t : 1));
      }
    } catch (e: any) {
      setActionMessage(e.message || "Failed to reject auction");
    }
  };

  const handleDeleteAuction = async (auctionId: string) => {
    if (!window.confirm("Delete this auction?")) return;

    try {
      setActionMessage(null);
      await adminAuctionService.deleteAuction(auctionId);
      setActionMessage("Auction deleted");
      setRefreshIndex((i) => i + 1);
    } catch (e: any) {
      setActionMessage(e.message || "Delete failed");
    }
  };

  const handleUpdateParticipantStatus = async (
    auctionId: string,
    participantId: string,
    status: string
  ) => {
    try {
      setActionMessage(null);
      const updated = await adminAuctionService.updateParticipantStatus(
        auctionId,
        participantId,
        status
      );
      setSelectedAuction(updated);
      setActionMessage("Participant status updated");
    } catch (e: any) {
      setActionMessage(e.message || "Failed to update participant");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const allCount = Object.values(statusCounts).reduce((s, n) => s + n, 0);

  const statusTabs: Array<{ value: typeof filterStatus; label: string; count: number | null }> = [
    { value: "all", label: "All", count: allCount || null },
    { value: "upcoming", label: "Upcoming", count: statusCounts.upcoming ?? 0 },
    { value: "active", label: "Live", count: statusCounts.live ?? 0 },
    { value: "completed", label: "Completed", count: statusCounts.completed ?? 0 },
    { value: "rejected", label: "Rejected", count: statusCounts.rejected ?? 0 },
  ];

  const time12 = (t?: string) => {
    if (!t || t === "—" || t === "N/A") return "";
    const [h, m] = t.split(":");
    const hh = parseInt(h, 10);
    if (isNaN(hh)) return "";
    return `${hh % 12 || 12}:${m} ${hh >= 12 ? "PM" : "AM"}`;
  };

  const TYPE_LABEL: Record<string, string> = {
    english_no_tie: "English (No Tie)",
    english: "English",
    traffic_light: "Traffic Light",
  };

  const money = (n: number, currency?: string) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency === "USD" ? "USD" : "INR",
      maximumFractionDigits: 0,
    }).format(n);

  const isLive = (a: Auction) => ["live", "active", "approved"].includes(a.status);

  const pageNumbers = (): Array<number | "…"> => {
    const pages: Array<number | "…"> = [];
    const add = (n: number | "…") => pages.push(n);
    const win = new Set([1, totalPages, page - 1, page, page + 1]);
    let last = 0;
    for (let n = 1; n <= totalPages; n++) {
      if (win.has(n)) {
        if (n - last > 1) add("…");
        add(n);
        last = n;
      }
    }
    return pages;
  };

  const changeStatus = (v: typeof filterStatus) => {
    setFilterStatus(v);
    setPage(1);
  };

  return (
    <div className="mau-root">
      {/* ---------- Header ---------- */}
      <header className="mau-hero">
        <div>
          <h1 className="mau-title">
            <span className="mau-title-icon">
              <Gavel />
            </span>
            Manage Auctions
          </h1>
          <p className="mau-subtitle">Review, monitor and manage every auction on the platform</p>
        </div>
        <div className="mau-hero-actions">
          <button type="button" className="mau-btn" onClick={() => setRefreshIndex((i) => i + 1)} disabled={loading}>
            <RefreshCcw className={loading ? "mau-spin" : ""} />
            Refresh
          </button>
          <button type="button" className="mau-btn mau-btn-primary" onClick={handleExportAuctions}>
            <Download />
            Export CSV
          </button>
        </div>
      </header>

      {actionMessage && (
        <div className="mau-banner mau-banner-info" role="status">
          <Check />
          <span>{actionMessage}</span>
          <button type="button" onClick={() => setActionMessage(null)} aria-label="Dismiss">
            <X />
          </button>
        </div>
      )}
      {error && (
        <div className="mau-banner mau-banner-error" role="alert">
          <AlertTriangle />
          <span>{error}</span>
          <button type="button" onClick={() => setRefreshIndex((i) => i + 1)}>
            Retry
          </button>
        </div>
      )}

      {/* ---------- Tabs + search ---------- */}
      <section className="mau-card mau-flush">
        <div className="mau-toolbar">
          <div className="mau-tabs" role="tablist" aria-label="Filter by status">
            {statusTabs.map((t) => (
              <button
                key={t.value}
                type="button"
                role="tab"
                aria-selected={filterStatus === t.value}
                className={`mau-tab ${filterStatus === t.value ? "active" : ""}`}
                onClick={() => changeStatus(t.value)}
              >
                {t.label}
                {t.count !== null && <span className="mau-tab-count">{t.count}</span>}
              </button>
            ))}
          </div>
          <div className="mau-search">
            <Search />
            <input
              type="text"
              placeholder="Search title, auction no, company or auctioneer..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        </div>

        {/* ---------- Table ---------- */}
        {loading && auctions.length === 0 ? (
          <div className="mau-state">
            <div className="mau-spinner" />
            <span>Loading auctions...</span>
          </div>
        ) : auctions.length === 0 ? (
          <div className="mau-state">
            <Gavel />
            <strong>No auctions found</strong>
            <span>Try a different status or search term.</span>
          </div>
        ) : (
          <div className={`mau-table-wrap ${loading ? "mau-fade" : ""}`}>
            <table className="mau-table">
              <thead>
                <tr>
                  <th>Auction</th>
                  <th>Auctioneer</th>
                  <th>Format</th>
                  <th className="r">Bids</th>
                  <th className="r">Participants</th>
                  <th>Status</th>
                  <th className="r">Actions</th>
                </tr>
              </thead>
              <tbody>
                {auctions.map((auction) => (
                  <tr key={auction.id}>
                    <td data-label="Auction">
                      <div className="mau-cell-title">{auction.title}</div>
                      <div className="mau-cell-sub">
                        {auction.auctionNo} &middot; {auction.startDate}
                        {time12(auction.auctionStartTime) && ` ${time12(auction.auctionStartTime)}`}
                        {auction.durationMinutes > 0 && ` · ${auction.durationMinutes} min`}
                      </div>
                    </td>
                    <td data-label="Auctioneer">
                      <div className="mau-cell-title">{auction.auctioneerName}</div>
                      <div className="mau-cell-sub">{auction.companyName}</div>
                    </td>
                    <td data-label="Format">
                      <span className={`mau-mode mau-mode-${auction.mode}`}>
                        {auction.mode === "forward" ? <ArrowUp /> : <ArrowDown />}
                        {auction.mode === "forward" ? "Forward" : "Reverse"}
                      </span>
                      <div className="mau-cell-sub">{TYPE_LABEL[auction.auctionType] || auction.auctionType}</div>
                    </td>
                    <td className="r" data-label="Bids">
                      {auction.totalBids > 0 ? (
                        <>
                          <div className="mau-cell-title">{money(auction.bestBid, auction.currency)}</div>
                          <div className="mau-cell-sub">
                            best of {auction.totalBids} bid{auction.totalBids === 1 ? "" : "s"}
                          </div>
                        </>
                      ) : (
                        <span className="mau-muted">No bids</span>
                      )}
                    </td>
                    <td className="r" data-label="Participants">
                      <div className="mau-cell-title">
                        {auction.joined_participants ?? 0}
                        <span className="mau-muted"> / {auction.total_participants ?? 0}</span>
                      </div>
                      <div className="mau-cell-sub">joined / invited</div>
                    </td>
                    <td data-label="Status">
                      <span className={`mau-badge mau-badge-${isLive(auction) ? "live" : auction.status}`}>
                        {isLive(auction) && <i className="mau-pulse" />}
                        {isLive(auction) ? "Live" : auction.status}
                      </span>
                    </td>
                    <td className="r" data-label="Actions">
                      <div className="mau-actions">
                        <button onClick={() => handleViewDetails(auction)} className="mau-icon-btn" title="View details" aria-label="View details">
                          <Eye />
                        </button>
                        {isLive(auction) && (
                          <button
                            onClick={() => window.open(`/admin/auction-live/${auction.id}`, "_blank")}
                            className="mau-icon-btn mau-icon-live"
                            title="Watch live"
                            aria-label="Watch live"
                          >
                            <Radio />
                          </button>
                        )}
                        {auction.status === "pending" && (
                          <>
                            <button onClick={() => handleApproveAuction(auction.id)} className="mau-icon-btn mau-icon-ok" title="Approve" aria-label="Approve">
                              <Check />
                            </button>
                            <button
                              onClick={() => handleRejectAuction(auction.id, "Admin rejected")}
                              className="mau-icon-btn mau-icon-danger"
                              title="Reject"
                              aria-label="Reject"
                            >
                              <X />
                            </button>
                          </>
                        )}
                        <button onClick={() => handleDeleteAuction(auction.id)} className="mau-icon-btn mau-icon-danger" title="Delete" aria-label="Delete">
                          <Trash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ---------- Pagination ---------- */}
        {total > 0 && (
          <div className="mau-pager">
            <span className="mau-pager-info">
              Showing {(page - 1) * limit + 1}-{(page - 1) * limit + auctions.length} of {total}
            </span>
            <div className="mau-pager-buttons">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Previous page">
                <ChevronLeft />
              </button>
              {pageNumbers().map((n, i) =>
                n === "…" ? (
                  <span key={`gap-${i}`} className="mau-pager-gap">
                    …
                  </span>
                ) : (
                  <button key={n} type="button" className={n === page ? "active" : ""} onClick={() => setPage(n)}>
                    {n}
                  </button>
                )
              )}
              <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} aria-label="Next page">
                <ChevronRight />
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ---------- Details modal ---------- */}
      {selectedAuction && (
        <div
          className="mau-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedAuction(null);
          }}
        >
          <div className="mau-modal" role="dialog" aria-modal="true" aria-label="Auction details" onClick={(e) => e.stopPropagation()}>
            <div className="mau-modal-head">
              <div>
                <h2>{selectedAuction.title}</h2>
                <p>
                  {selectedAuction.auctionNo} &middot;{" "}
                  <span className={`mau-badge mau-badge-${isLive(selectedAuction) ? "live" : selectedAuction.status}`}>
                    {isLive(selectedAuction) ? "Live" : selectedAuction.status}
                  </span>
                </p>
              </div>
              <button type="button" className="mau-icon-btn" onClick={() => setSelectedAuction(null)} aria-label="Close">
                <X />
              </button>
            </div>

            <div className="mau-modal-body">
              {detailLoading && <div className="mau-modal-loading">Loading details...</div>}

              <div className="mau-tiles">
                <div className="mau-tile">
                  <span>Best bid</span>
                  <strong>{selectedAuction.totalBids > 0 ? money(selectedAuction.bestBid, selectedAuction.currency) : "No bids"}</strong>
                </div>
                <div className="mau-tile">
                  <span>Total bids</span>
                  <strong>{selectedAuction.totalBids}</strong>
                </div>
                <div className="mau-tile">
                  <span>Participants</span>
                  <strong>
                    {selectedAuction.joined_participants ?? 0} / {selectedAuction.total_participants ?? selectedAuction.participants.length}
                  </strong>
                </div>
                <div className="mau-tile">
                  <span>Format</span>
                  <strong>{selectedAuction.mode === "forward" ? "Forward" : "Reverse"}</strong>
                </div>
              </div>

              <div className="mau-sections">
                <section className="mau-section">
                  <h3>Auction</h3>
                  <dl>
                    <div><dt>Type</dt><dd>{TYPE_LABEL[selectedAuction.auctionType] || selectedAuction.auctionType}</dd></div>
                    <div><dt>{selectedAuction.mode === "forward" ? "Increment" : "Decrement"}</dt><dd>{money(selectedAuction.decrementalValue, selectedAuction.currency)}</dd></div>
                    <div><dt>Auto extension</dt><dd>{selectedAuction.autoExtension ? "Yes" : "No"}</dd></div>
                    {selectedAuction.description && (
                      <div className="mau-dl-wide"><dt>Description</dt><dd>{selectedAuction.description}</dd></div>
                    )}
                  </dl>
                </section>

                <section className="mau-section">
                  <h3>Schedule</h3>
                  <dl>
                    <div>
                      <dt>Start</dt>
                      <dd>
                        {selectedAuction.startDate} {time12(selectedAuction.auctionStartTime) && `at ${time12(selectedAuction.auctionStartTime)}`}
                      </dd>
                    </div>
                    <div>
                      <dt>End</dt>
                      <dd>
                        {selectedAuction.endDate || "N/A"} {time12(selectedAuction.auctionEndTime) && `at ${time12(selectedAuction.auctionEndTime)}`}
                      </dd>
                    </div>
                    <div><dt>Duration</dt><dd>{selectedAuction.durationMinutes ? `${selectedAuction.durationMinutes} minutes` : "N/A"}</dd></div>
                  </dl>
                </section>

                <section className="mau-section">
                  <h3>Auctioneer</h3>
                  <dl>
                    <div><dt>Name</dt><dd>{selectedAuction.auctioneerName}</dd></div>
                    <div><dt>Company</dt><dd>{selectedAuction.companyName}</dd></div>
                    <div><dt>Phone</dt><dd>{selectedAuction.auctioneer_phone}</dd></div>
                  </dl>
                </section>

                <section className="mau-section">
                  <h3>Documents ({selectedAuction.documents.length})</h3>
                  {selectedAuction.documents.length === 0 ? (
                    <p className="mau-muted">No documents uploaded</p>
                  ) : (
                    <ul className="mau-docs">
                      {selectedAuction.documents.map((doc, index) => (
                        <li key={index}>
                          <FileText />
                          <span>{doc.file_name}</span>
                          {doc.file_path && (
                            <div className="mau-doc-actions">
                              <button type="button" className="mau-icon-btn" onClick={() => handleDocumentDownload(doc)} title="Download" aria-label="Download">
                                <Download />
                              </button>
                              <a
                                href={doc.file_path.startsWith("http") ? doc.file_path : `${BACKEND_URL}/${doc.file_path}`}
                                target="_blank"
                                rel="noreferrer"
                                className="mau-icon-btn"
                                title="Open"
                                aria-label="Open"
                              >
                                <ExternalLink />
                              </a>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section className="mau-section mau-section-wide">
                  <h3>Participants ({selectedAuction.participants.length})</h3>
                  {selectedAuction.participants.length === 0 ? (
                    <p className="mau-muted">No participants invited</p>
                  ) : (
                    <div className="mau-table-wrap">
                      <table className="mau-table mau-table-compact">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Company</th>
                            <th>Phone</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedAuction.participants.map((participant) => (
                            <tr key={participant.id}>
                              <td>{participant.name}</td>
                              <td>{participant.company}</td>
                              <td>{participant.phone || "N/A"}</td>
                              <td>
                                <span className={`mau-badge mau-badge-${participant.status}`}>{participant.status}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>

                {(selectedAuction.adminNotes || selectedAuction.rejectionReason) && (
                  <section className="mau-section mau-section-wide">
                    <h3>
                      <AlertTriangle /> Admin notes
                    </h3>
                    {selectedAuction.adminNotes && (
                      <div className="mau-note">
                        <strong>Notes:</strong> {selectedAuction.adminNotes}
                      </div>
                    )}
                    {selectedAuction.rejectionReason && (
                      <div className="mau-note mau-note-danger">
                        <strong>Rejection reason:</strong> {selectedAuction.rejectionReason}
                      </div>
                    )}
                  </section>
                )}
              </div>
            </div>

            <div className="mau-modal-foot">
              {isLive(selectedAuction) && (
                <button type="button" className="mau-btn" onClick={() => window.open(`/admin/auction-live/${selectedAuction.id}`, "_blank")}>
                  <Radio />
                  Watch live
                </button>
              )}
              {selectedAuction.status === "pending" && (
                <button
                  type="button"
                  className="mau-btn mau-btn-ok"
                  onClick={() => {
                    handleApproveAuction(selectedAuction.id);
                    setSelectedAuction(null);
                  }}
                >
                  <Check />
                  Approve
                </button>
              )}
              {selectedAuction.status !== "rejected" && selectedAuction.status !== "completed" && (
                <button
                  type="button"
                  className="mau-btn mau-btn-danger"
                  onClick={() => {
                    handleCloseAuction(selectedAuction.id);
                    setSelectedAuction(null);
                  }}
                >
                  <X />
                  Reject
                </button>
              )}
              <button type="button" className="mau-btn" onClick={() => setSelectedAuction(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageAuctions;
